import { useEffect, useMemo, useState } from "react";

import { useBrandGroupsQuery, useBrandsQuery } from "modules/BrandsDashboard/hooks";
import { isGroupValue, sortByName, toGroupValue } from "modules/BrandsDashboard/utils";

import type { DropdownItemProps } from "components";
import type { Brand } from "models";

type UseBrandGroupsDropdownProps = {
  selectedVinPrefixes: string[];
  handleOnChange: (vinPrefixes: string[]) => void;
};

const createBrandDropdownOptions = (brands: Brand[], className?: string) =>
  brands.map(brand => ({
    key: brand.vin_prefix,
    value: brand.vin_prefix,
    text: `${brand.name} - ${brand.vin_prefix}`,
    className
  }));

const isGroupSelected = (vinPrefixes: string[], selectedVinPrefixSet: Set<string>) =>
  vinPrefixes.length > 0 && vinPrefixes.every(vinPrefix => selectedVinPrefixSet.has(vinPrefix));

export const useBrandGroupsDropdown = ({ selectedVinPrefixes, handleOnChange }: UseBrandGroupsDropdownProps) => {
  const [selectedGroupValues, setSelectedGroupValues] = useState<string[]>([]);

  const { data: brands = [] } = useBrandsQuery();
  const { data: brandGroups = [] } = useBrandGroupsQuery();

  const selectedVinPrefixSet = useMemo(() => new Set(selectedVinPrefixes), [selectedVinPrefixes]);

  const brandsByGroupId = useMemo(() => {
    const map = new Map<number, Brand[]>();
    brands.forEach(brand => {
      if (!brand.brand_group_id) return;
      map.set(brand.brand_group_id, [...(map.get(brand.brand_group_id) ?? []), brand]);
    });
    return map;
  }, [brands]);

  const groupVinPrefixesByValue = useMemo(
    () => new Map(brandGroups.map(group => [toGroupValue(group.id), (brandsByGroupId.get(group.id) ?? []).map(brand => brand.vin_prefix)])),
    [brandGroups, brandsByGroupId]
  );

  const groupedOptions = useMemo(() => {
    const groupOptions = [...brandGroups].sort(sortByName).flatMap<DropdownItemProps<string>>(group => {
      const groupBrands = [...(brandsByGroupId.get(group.id) ?? [])].sort(sortByName);
      if (!groupBrands.length) return [];

      return [
        { key: toGroupValue(group.id), value: toGroupValue(group.id), text: group.name, icon: "circle g", className: "brand-group-header" },
        ...createBrandDropdownOptions(groupBrands, "brand-group-item")
      ];
    });
    const ungroupedOptions = createBrandDropdownOptions(brands.filter(brand => !brand.brand_group_id).sort(sortByName));

    return [...groupOptions, ...ungroupedOptions];
  }, [brands, brandGroups, brandsByGroupId]);

  const selectedValue = useMemo(() => {
    const activeGroupValues = selectedGroupValues.filter(groupValue => isGroupSelected(groupVinPrefixesByValue.get(groupValue) ?? [], selectedVinPrefixSet));
    const vinPrefixesInGroup = new Set(activeGroupValues.flatMap(groupValue => groupVinPrefixesByValue.get(groupValue) ?? []));

    return [...activeGroupValues, ...selectedVinPrefixes.filter(vinPrefix => !vinPrefixesInGroup.has(vinPrefix))];
  }, [selectedVinPrefixes, selectedVinPrefixSet, selectedGroupValues, groupVinPrefixesByValue]);

  const options = useMemo(() => {
    const collapsedGroupValues = new Set(selectedValue.filter(isGroupValue));
    const vinPrefixesInCollapsedGroup = new Set([...collapsedGroupValues].flatMap(groupValue => groupVinPrefixesByValue.get(groupValue) ?? []));

    return groupedOptions.filter(option =>
      isGroupValue(option.value)
        ? collapsedGroupValues.has(option.value) || !isGroupSelected(groupVinPrefixesByValue.get(option.value) ?? [], selectedVinPrefixSet)
        : !vinPrefixesInCollapsedGroup.has(option.value)
    );
  }, [groupedOptions, selectedValue, selectedVinPrefixSet, groupVinPrefixesByValue]);

  useEffect(() => {
    const missingVinPrefixes = selectedGroupValues
      .flatMap(groupValue => groupVinPrefixesByValue.get(groupValue) ?? [])
      .filter(vinPrefix => !selectedVinPrefixSet.has(vinPrefix));

    if (missingVinPrefixes.length) handleOnChange([...new Set([...selectedVinPrefixes, ...missingVinPrefixes])]);
  }, [selectedGroupValues, groupVinPrefixesByValue, selectedVinPrefixSet, selectedVinPrefixes, handleOnChange]);

  const handleOnSelectionChange = (selectedValues: string[]) => {
    setSelectedGroupValues(selectedValues.filter(isGroupValue));
    const vinPrefixes = selectedValues.flatMap(value => groupVinPrefixesByValue.get(value) ?? [value]);
    handleOnChange([...new Set(vinPrefixes)]);
  };

  return { options, selectedValue, handleOnSelectionChange };
};
